import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { Skeleton } from 'components/UnvUI'
import { TextField, MenuItem } from '@mui/material'

import $api from 'http/index'

import styles from './CuratorDrilldownPage.module.scss'

const KPI = (label, value, unit) => ({ label, value, unit })

const LineChart = ({ points = [], height = 160 }) => {
  const { t } = useTranslation('common')

  if (points.length === 0) {
    return <div className="unv-empty">{t('common.no_data')}</div>
  }
  const width = Math.max(points.length * 40, 200)
  const maxY = 100
  const stepX = points.length > 1 ? width / (points.length - 1) : 0
  const path = points
    .map((p, i) => {
      const x = i * stepX
      const y = height - (Math.min(maxY, p.value) / maxY) * (height - 20) - 10
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`
    })
    .join(' ')
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
      <line x1="0" y1={height - 10} x2={width} y2={height - 10} stroke="#E5E5E5" />
      <path d={path} fill="none" stroke="#FF7A00" strokeWidth="2.5" />
      {points.map((p, i) => {
        const x = i * stepX
        const y = height - (Math.min(maxY, p.value) / maxY) * (height - 20) - 10
        return <circle key={i} cx={x} cy={y} r="3" fill="#FF7A00" />
      })}
    </svg>
  )
}

const BarChart = ({ bars = [], height = 200 }) => {
  const { t } = useTranslation('common')

  if (bars.length === 0) {
    return <div className="unv-empty">{t('common.no_data')}</div>
  }
  const width = Math.max(bars.length * 70, 220)
  const maxY = Math.max(...bars.map((b) => b.value), 5)
  const barW = width / bars.length - 10
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
      <line x1="0" y1={height - 20} x2={width} y2={height - 20} stroke="#E5E5E5" />
      {bars.map((b, i) => {
        const h = (b.value / maxY) * (height - 40)
        const x = i * (barW + 10) + 5
        const y = height - 20 - h
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={h} fill="#FF7A00" rx="3" />
            <text x={x + barW / 2} y={height - 5} fontSize="11" textAnchor="middle" fill="#555">
              {b.label}
            </text>
            <text x={x + barW / 2} y={y - 4} fontSize="11" textAnchor="middle" fill="#333">
              {b.value.toFixed(1)}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

const CuratorDrilldownPage = () => {
  const { t } = useTranslation('common')
  const [groups, setGroups] = useState([])
  const [groupId, setGroupId] = useState('')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    const loadGroups = async () => {
      try {
        const res = await $api.get('/groups/')
        setGroups(res.data?.results || res.data || [])
      } catch {
        setGroups([])
      }
    }
    loadGroups()
  }, [])

  useEffect(() => {
    if (!groupId) return
    const load = async () => {
      setLoading(true)
      setError(false)
      try {
        const res = await $api.get('/admin-panel/drilldown/', { params: { group_id: groupId } })
        setData(res.data)
      } catch {
        setError(true)
        setData(null)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [groupId])

  const kpis = data
    ? [
        KPI('Средний балл', data.avg_grade != null ? Number(data.avg_grade).toFixed(2) : '—', ''),
        KPI('Посещаемость', data.attendance_pct != null ? `${Number(data.attendance_pct).toFixed(1)}` : '—', '%'),
        KPI('Должников', data.debtors_count ?? '—', 'чел.'),
        KPI('Студентов', data.students?.length ?? data.students_count ?? '—', 'чел.'),
      ]
    : []

  return (
    <div className={styles.page}>
      <div className={`unv-card ${styles.header}`}>
        <h1 className={styles.title}>{t('auto.deandashboard_curatordrilldownpage.k1')}</h1>
        <TextField
          select
          size="small"
          label={t('schedule.group')}
          value={groupId}
          onChange={(e) => setGroupId(e.target.value)}
          className={styles.groupSelect}
        >
          <MenuItem value="">Не выбрана</MenuItem>
          {groups.map((g) => (
            <MenuItem key={g.id} value={g.id}>
              {g.name || g.title}
            </MenuItem>
          ))}
        </TextField>
      </div>

      {!groupId ? (
        <div className={`unv-card ${styles.content}`}>
          <div className="unv-empty">{t('auto.deandashboard_curatordrilldownpage.k2')}</div>
        </div>
      ) : loading ? (
        <div className={`unv-card ${styles.content}`}>
          <div className={styles.loaderWrap}>
            <Skeleton variant="table" rows={5} cols={4} />
          </div>
        </div>
      ) : error || !data ? (
        <div className={`unv-card ${styles.content}`}>
          <div className="unv-empty">{t('schedule.section_in_dev')}</div>
        </div>
      ) : (
        <>
          <div className={styles.kpiGrid}>
            {kpis.map((k) => (
              <div key={k.label} className={`unv-card ${styles.kpiCard}`}>
                <div className={styles.kpiLabel}>{k.label}</div>
                <div className={styles.kpiValue}>
                  {k.value}
                  {k.unit && <span className={styles.kpiUnit}> {k.unit}</span>}
                </div>
              </div>
            ))}
          </div>

          <div className={styles.chartsRow}>
            <div className={`unv-card ${styles.chartCard}`}>
              <h3>{t('auto.deandashboard_curatordrilldownpage.k3')}</h3>
              <LineChart points={data.attendance_weekly || []} />
            </div>
            <div className={`unv-card ${styles.chartCard}`}>
              <h3>{t('auto.deandashboard_curatordrilldownpage.k4')}</h3>
              <BarChart bars={data.avg_by_subject || []} />
            </div>
          </div>

          <div className={`unv-card ${styles.content}`}>
            <h3>{t('menu.students')}</h3>
            {(data.students || []).length === 0 ? (
              <div className="unv-empty">{t('auto.deandashboard_curatordrilldownpage.k5')}</div>
            ) : (
              <table className="unv-table">
                <thead>
                  <tr>
                    <th>{t('common.fullName')}</th>
                    <th>{t('dashboard.averageScore')}</th>
                    <th>{t('dashboard.attendance')}</th>
                    <th>{t('auto.deandashboard_curatordrilldownpage.k6')}</th>
                    <th>{t('auto.deandashboard_curatordrilldownpage.k7')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.students || []).map((s) => (
                    <tr key={s.id}>
                      <td>{s.full_name || '—'}</td>
                      <td>{s.avg_grade != null ? Number(s.avg_grade).toFixed(2) : '—'}</td>
                      <td>
                        {s.attendance_pct != null ? `${Number(s.attendance_pct).toFixed(1)}%` : '—'}
                      </td>
                      <td>{s.missing_assignments ?? '—'}</td>
                      <td>{s.absences ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default CuratorDrilldownPage
